import { useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { useGlobalNavUserProfileClickActions } from "@/components/global-nav/user-profile-new/src/user-profile";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { LogoV2 as Logo } from "@/libraries/wpl/foundations/src";

import AutoCompleteSearchBar from "../auto-complete-search-bar";
import { useMobileNavDisplay } from "../hooks/use-mobile-nav-display";

import { Divider } from "./icons/divider";
import {
    MobileNavLinks,
    MobileNavLinksContainerInfo
} from "./mobile-nav-links-container";
import MobileLoggedInUseDropdown from "./mobile-nav-logged-in-dropdown";
import { MobileNavTop } from "./mobile-nav-top";

const containerCss = css({
    "background": "#fff",
    "display": "flex",
    "flexDirection": "column",
    "gap": token("space.150"),

    ".search-bar": {
        "marginInlineStart": "25px",
        "marginInlineEnd": "25px",
        "width": "100%",

        ".aa-Panel": {
            width: "auto"
        }
    }
});

const containerClosedCss = css({
    padding: "8px",
    [media.above.sm]: {
        padding: "0"
    }
});

const containerOpenCss = css({
    [media.below.sm]: {
        height: "100vh",
        width: "100%",
        overflow: "auto",
        position: "fixed",
        insetBlockStart: "0",
        padding: "8px 24px 24px 24px",
        zIndex: "5"
    },
    padding: `0`
});

const navBarClosedCss = css({
    paddingInline: token("space.200"),
    [media.above.sm]: {
        paddingInlineStart: "0",
        paddingInlineEnd: token("space.200")
    },
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
});

const navBarOpenCss = css({
    [media.above.sm]: {
        paddingInlineStart: "0",
        paddingInlineEnd: token("space.200")
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
});

const menuContentStyle = css({
    display: "block",
    width: "auto",

    [media.above.sm]: {
        display: "none"
    }
});

const fadeInCss = css({
    "@keyframes fadeIn": {
        from: {
            opacity: "0"
        },
        to: {
            opacity: "1"
        }
    },
    "opacity": "0",
    "animation": "0.4s ease-in fadeIn 200ms forwards"
});

const slideInLeftCss = css({
    "@keyframes slideIn": {
        from: {
            transform: "translateX(-100%)",
            opacity: "0"
        },
        to: {
            transform: "translateX(0%)",
            opacity: "1"
        }
    },
    "opacity": "0",
    "animation": "0.5s slideIn forwards"
});

const slideInRightCss = css({
    "@keyframes slideIn": {
        from: {
            transform: "translateX(0%)",
            opacity: "0"
        },
        to: {
            transform: "translateX(100%)",
            opacity: "1"
        }
    },
    "opacity": "0",
    "animation": "0.5s slideIn forwards"
});

export type DropdownType = "default" | "search";

export type MobileGlobalNavProps = {
    productKey: ProductKeyTypes;
    mobileLinksSections: MobileNavLinksContainerInfo[];
    mobileLogo?: JSX.Element;
    mobileLogoAriaLabel?: string;
    mobileLogoHref?: string;
    mobileLogoDataEventContainer?: string;
    isIncludeCTA?: boolean;
    ctaText?: string;
    ctaUrl?: string;
    ctaOnClick?: () => void;
    isIncludeAtlassianLogo?: boolean;
    isShowTryNowLink?: boolean;
    additionalMenuPageObjectsArray?: AdditionalMenuPageObjects[];
};

export type MobileGlobalNavMenuProps = {
    isDisplayLoggedInUseDropdown: boolean;
};

export type AdditionalMenuPageObjects = {
    menuPageKey: string;
    menuPageObjects: JSX.Element;
};

type addedMenuPagesObjectType = {
    [key: string]: JSX.Element;
};

const MobileNavProductPageDropdownMenu = ({
    isUserLoggedIn,
    menuPage = "default",
    firstOpen,
    handleMenuPage,
    mobileLinksSections,
    productKey,
    isIncludeCTA = true,
    ctaText = "Get it free",
    ctaUrl = "/software/jira/free",
    ctaOnClick,
    isIncludeAtlassianLogo = true,
    additionalMenuPageObjectsArray = []
}: {
    readonly isUserLoggedIn: boolean;
    readonly menuPage: DropdownType;
    readonly firstOpen: boolean;
    handleMenuPage: (menuPage: DropdownType | string) => void;
    readonly mobileLinksSections: MobileNavLinksContainerInfo[];
    readonly productKey: ProductKeyTypes;
    readonly isIncludeCTA?: boolean;
    readonly ctaText?: string;
    readonly ctaUrl?: string;
    readonly ctaOnClick?: () => void;
    readonly isIncludeAtlassianLogo?: boolean;
    readonly additionalMenuPageObjectsArray?: AdditionalMenuPageObjects[];
}) => {
    const MobileNavLinksMenuPage: JSX.Element = (
        <MobileNavLinks
            isLoggedIn={isUserLoggedIn}
            handleMenuPage={handleMenuPage}
            mobileLinksSections={mobileLinksSections}
            productKey={productKey}
            isIncludeCTA={isIncludeCTA}
            ctaText={ctaText}
            ctaUrl={ctaUrl}
            ctaOnClick={ctaOnClick}
            isIncludeAtlassianLogo={isIncludeAtlassianLogo}
        />
    );
    const defaultSetOfMenuPages = {
        default: MobileNavLinksMenuPage,
        search: <AutoCompleteSearchBar isMobileView={true} />
    };

    let menuPagesObject = { ...defaultSetOfMenuPages };

    if (additionalMenuPageObjectsArray.length > 0) {
        const addedMenuPagesObject: addedMenuPagesObjectType = {};
        for (const additionalMenuPageObject of additionalMenuPageObjectsArray) {
            const { menuPageKey, menuPageObjects } = additionalMenuPageObject;
            addedMenuPagesObject[menuPageKey] = menuPageObjects;
        }
        menuPagesObject = { ...menuPagesObject, ...addedMenuPagesObject };
    }

    return menuPage === "default" ? (
        <div
            css={[firstOpen ? fadeInCss : slideInRightCss]}
            className="inner-mobile-dropdown-menu"
        >
            {menuPagesObject[menuPage]}
        </div>
    ) : (
        <div css={slideInLeftCss} className="inner-mobile-dropdown-menu">
            {menuPagesObject[menuPage]}
        </div>
    );
};

const defaultJiraLogo = (
    <Logo
        brand="jira"
        type="logo"
        appearance="brand"
        height={"25.6px"}
        width={"60px"}
    />
);

export const MobileProductPageNav = ({
    productKey,
    mobileLogo = defaultJiraLogo,
    mobileLogoAriaLabel = "Jira logo",
    mobileLogoHref = "/",
    mobileLogoDataEventContainer = "ProductPageNavJiraV2",
    mobileLinksSections,
    isIncludeCTA = true,
    ctaText = "Get it free",
    ctaUrl = "/software/jira/free",
    ctaOnClick,
    isIncludeAtlassianLogo = true,
    isShowTryNowLink = true,
    additionalMenuPageObjectsArray = []
}: MobileGlobalNavProps) => {
    const intl = useIntl();
    const [isOpenSearch, setIsOpenSearch] = useState(false);

    const {
        userInfo,
        showMenu,
        isDisplayLoggedInUseDropdown,
        firstOpen,
        menuPage,
        handleToggleMenu,
        handleFirstOpen,
        handleMenuPage,
        handleClickAvatar
    } = useMobileNavDisplay(isOpenSearch);

    const {
        onSwitchAccountClick,
        onManageProfileClick,
        onManageLicensesClick,
        onLogOutClick
    } = useGlobalNavUserProfileClickActions({ email: userInfo.email });

    const mobileGlobalNavDropdownMenu = () => (
        <MobileNavProductPageDropdownMenu
            isUserLoggedIn={userInfo.isUserLoggedIn}
            menuPage={menuPage as DropdownType}
            firstOpen={firstOpen}
            handleMenuPage={handleMenuPage}
            mobileLinksSections={mobileLinksSections}
            productKey={productKey}
            isIncludeCTA={isIncludeCTA}
            ctaText={ctaText}
            ctaUrl={ctaUrl}
            ctaOnClick={ctaOnClick}
            isIncludeAtlassianLogo={isIncludeAtlassianLogo}
            additionalMenuPageObjectsArray={additionalMenuPageObjectsArray}
        />
    );

    return (
        <div
            css={[
                containerCss,
                showMenu ? containerOpenCss : containerClosedCss
            ]}
        >
            <div css={[showMenu ? navBarOpenCss : navBarClosedCss]}>
                <MobileNavTop
                    productKey={productKey}
                    isLoggedIn={userInfo.isUserLoggedIn}
                    userName={userInfo.name ?? ""}
                    picture={userInfo.picture ?? ""}
                    handleClickAvatar={handleClickAvatar}
                    showMenu={showMenu}
                    handleToggleMenu={handleToggleMenu}
                    menuPage={menuPage as DropdownType}
                    handleMenuPage={handleMenuPage}
                    handleFirstOpen={handleFirstOpen}
                    mobileLogo={mobileLogo}
                    mobileLogoAriaLabel={mobileLogoAriaLabel}
                    mobileLogoHref={mobileLogoHref}
                    mobileLogoDataEventContainer={mobileLogoDataEventContainer}
                    isOpenSearch={isOpenSearch}
                    setIsOpenSearch={setIsOpenSearch}
                    tabletDropdownMenu={mobileGlobalNavDropdownMenu()}
                    isProductPage={true}
                    isShowTryNowLink={isShowTryNowLink}
                    navigationTrynowCtaText={intl.formatMessage({
                        id: "productpage.try-now-button-label",
                        defaultMessage: "Get it free"
                    })}
                    navigationTrynowCtaUrl={"/software/jira/free"}
                    navigationTrynowCtaOnClick={ctaOnClick}
                />
            </div>
            {showMenu && (
                <div css={menuContentStyle} id="nav-mobile-dropdown-menu">
                    <div>
                        <Divider />
                    </div>
                    {isDisplayLoggedInUseDropdown ? (
                        <MobileLoggedInUseDropdown
                            userInfo={{
                                displayName: userInfo.name || "",
                                email: userInfo.email || "",
                                avatar: userInfo.picture || ""
                            }}
                            onLogOutClick={onLogOutClick}
                            onManageLicensesClick={onManageLicensesClick}
                            onManageProfileClick={onManageProfileClick}
                            onSwitchAccountClick={onSwitchAccountClick}
                        />
                    ) : (
                        mobileGlobalNavDropdownMenu()
                    )}
                </div>
            )}
        </div>
    );
};
